<template>
    <!-- `shrink-0 whitespace-nowrap` lives HERE, not on the call site: this
         component has two root nodes (the button and its popover), so Vue has no
         single root to forward a parent's `class` to and silently drops it. The
         chat header is a tight one-line row — without this the label wrapped and
         took the whole bar with it (MYAC-1327). -->
    <Button data-testid="saved-prompts-button"
        class="shrink-0 whitespace-nowrap"
        v-tooltip.bottom="compact ? label : ''"
        icon="pi pi-book" :label="compact ? undefined : label" text size="small" :severity="severity"
        :aria-label="t('chat.savedPrompts.title')" aria-haspopup="dialog"
        @click="toggle" />
    <Popover ref="pop" :pt="{ content: { class: 'p-2' } }" @hide="query = ''">
        <div data-testid="saved-prompts-popover" class="w-[22rem] max-w-[85vw] flex flex-col gap-2">
            <div class="flex items-center justify-between px-1">
                <span class="text-sm font-semibold">{{ t('chat.savedPrompts.title') }}</span>
                <!-- MYAC-1140: goes to the prompts PAGE. DISABLED-with-a-reason on a
                     project type that has no chat page to hang the pages off:
                     there the concept applies, only the destination is missing. -->
                <span
                    v-tooltip.left="managePath ? '' : t('chat.savedPrompts.pagesUnavailable')"
                    class="inline-flex">
                    <Button data-testid="saved-prompts-manage" :label="t('chat.savedPrompts.manage')"
                        link size="small" :disabled="!managePath" @click="onManage" />
                </span>
            </div>
            <InputText v-if="prompts.length > 6" v-model="query" data-testid="saved-prompts-search"
                :placeholder="t('chat.savedPrompts.search')" size="small" class="w-full" />
            <p v-if="loadFailed" class="px-1 text-sm text-red-600">{{ t('chat.savedPrompts.loadFailed') }}</p>
            <p v-else-if="!hasLoaded" class="px-1 text-sm text-surface-500">{{ t('chat.savedPrompts.loading') }}</p>
            <div v-else class="max-h-[50vh] overflow-y-auto">
                <SavedPromptsList :prompts="prompts"
                    :query="prompts.length > 6 ? query : ''" @use="onUse" />
            </div>
        </div>
    </Popover>
</template>

<script setup lang="ts">
/**
 * SavedPromptsButton (MYAC-1106) — the always-visible "Vos prompts enregistrés"
 * button in the chat panel header, with the popover that lists the project's
 * prompts. It only relays `use` — the parent prefills. The same component is
 * rendered in the header mid-conversation and in the middle of a fresh session.
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Popover from 'primevue/popover'
import { useRouter } from 'vue-router'
import SavedPromptsList from './SavedPromptsList.vue'
import type { SavedPrompt } from '@/models/SavedPrompt'
import { useChatScope } from '@/composables/useChatScope'
import { promptsListRouteForProject } from '@/router/savedPromptRoutes'

const props = defineProps<{
    prompts: SavedPrompt[]
    loadFailed: boolean
    hasLoaded: boolean
    /** Drop the text label, keep the icon — a narrow host has no room for it.
     *  The name survives in the tooltip and the aria-label. */
    compact?: boolean
    /** The style of the « New session » button beside it (MYAC-1522): one look for the session tools. */
    severity?: 'secondary'
}>()
const emit = defineEmits<{ (e: 'use', prompt: SavedPrompt): void }>()

const { t } = useI18n()
const router = useRouter()
// Saved prompts are the chat's: they follow the project the chat is about.
const { scopeProject } = useChatScope()
const pop = ref<InstanceType<typeof Popover>>()
const query = ref('')

/** Null on a project type with no chat page (regulatory-monitoring). */
const managePath = computed(() => promptsListRouteForProject(scopeProject.value))

/** « Vos prompts », short like « Nouvelle session » beside it (MYAC-1522); the full
 *  name stays in the aria-label and the popover's title. */
const label = computed(() => t('chat.savedPrompts.button'))

const toggle = (event: Event) => pop.value?.toggle(event)
/** Every action closes the popover first — it is an overlay, and leaving it
 * open behind a dialog traps focus between the two. */
const closePopover = () => {
    pop.value?.hide()
    query.value = ''
}

const onUse = (p: SavedPrompt) => {
    closePopover()
    emit('use', p)
}

const onManage = () => {
    closePopover()
    if (managePath.value) router.push(managePath.value)
}
</script>
